import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
} from 'recharts';
import { Card, CardHeader, CardBody } from '../../ui/Card';
import { PEER_COLORS, BAR_MAX_SIZE, gridProps, xAxisProps, zeroLineProps, CHART_INK, CHART_TYPE } from './chartTheme';
import { isEstimatedValue, ESTIMATED_FILL_OPACITY } from './chartNotes';
import { formatFiscalYear, formatCurrencyValue } from '../utils/formatters';
import type { UnifiedFinancialRecord, RegionConfig } from '../../../types/financial';

interface Props {
  data: UnifiedFinancialRecord[];
  region?: RegionConfig;
}

export default function EBITDAComparisonChart({ data, region }: Props) {
  const chartData = data.map((d) => ({
    year: formatFiscalYear(d.year, region),
    ebitda: d.ebitda,
    ebit: d.ebit,
    // Reported operating figures — only lighten if a note actually calls them
    // into question (field-aware), so a note about an estimated price leaves
    // these solid.
    ebitdaEstimated: isEstimatedValue(d.data_notes, 'ebitda'),
    ebitEstimated: isEstimatedValue(d.data_notes, 'ebit'),
  }));

  return (
    <Card>
      <CardHeader>
        <h3 className="text-sm font-semibold text-neutral-800">
          EBITDA vs EBIT ({region?.currency?.unit_abbr})
        </h3>
        <p className="text-xs text-neutral-500 mt-0.5">Absolute EBITDA and EBIT comparison over time</p>
      </CardHeader>
      <CardBody>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={chartData} margin={{ top: 20, right: 16, left: 8, bottom: 0 }}>
            <CartesianGrid {...gridProps} />
            <XAxis dataKey="year" {...xAxisProps} />
            <YAxis
              width={80}
              domain={['auto', 'auto']}
              tick={{ fontSize: CHART_TYPE.axisTick, fill: CHART_INK.label }}
              tickFormatter={(v) => formatCurrencyValue(v, region, true)}
            />
            <Tooltip
              formatter={(value: number, name: string) => [
                formatCurrencyValue(value, region),
                name,
              ]}
            />
            <Legend />
            <ReferenceLine y={0} {...zeroLineProps} />
            <Bar dataKey="ebitda" name="EBITDA" fill={PEER_COLORS[0]} radius={[3, 3, 0, 0]} maxBarSize={BAR_MAX_SIZE}>
              {chartData.map((d, i) => (
                <Cell key={i} fillOpacity={d.ebitdaEstimated ? ESTIMATED_FILL_OPACITY : 1} />
              ))}
            </Bar>
            <Bar dataKey="ebit" name="EBIT" fill={PEER_COLORS[2]} radius={[3, 3, 0, 0]} maxBarSize={BAR_MAX_SIZE}>
              {chartData.map((d, i) => (
                <Cell key={i} fillOpacity={d.ebitEstimated ? ESTIMATED_FILL_OPACITY : 1} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </CardBody>
    </Card>
  );
}
